// MARK://dhk8/admin-shell/pages/modules/WaitlistPage
// Waitlist (cola de espera) admin view. Lives at /waitlist.
//
// Lane #163 — gemelo de ReservationsPage para PosWaitlistEntry.
// Permite al gerente:
//   - Ver cola activa (WAITING + NOTIFIED) + historia del día
//   - Agregar manualmente (walk-in que no usó queue.nintaigo.com)
//   - Marcar Notified (mesa lista, llamar al cliente)
//   - Quitar de la cola (LEFT)
//   - Ver tiempos de espera promedio
//
// El POS tiene `WaitlistPanel` sidebar paralelo; admin es la vista amplia
// con historia + estadísticas.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, RefreshCw, Users, Phone, Clock, Bell, Trash2,
  History, ChevronRight, Armchair, ArrowUp, ArrowDown, PhoneOff,
} from 'lucide-react'

import * as wl from '../../lib/clients/waitlist.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { ShareLinkButton } from '../../components/ShareLinkButton.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { SeatDialog } from './WaitlistDetailPage.js'

const POLL_MS = 20_000

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
}
function minutesSince(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
}

function statusLabel(s: wl.WaitlistStatus): string {
  switch (s) {
    case 'WAITING': return 'Esperando'
    case 'NOTIFIED': return 'Notificada'
    case 'SEATED': return 'Sentada'
    case 'LEFT': return 'Se fue'
    case 'EXPIRED': return 'Expiró'
  }
}
function statusBadgeClass(s: wl.WaitlistStatus): string {
  switch (s) {
    case 'WAITING': return 'adm-badge'
    case 'NOTIFIED': return 'adm-badge adm-badge--warn'
    case 'SEATED': return 'adm-badge adm-badge--ok'
    default: return 'adm-badge adm-badge--muted'
  }
}

export default function WaitlistPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useConfirm()
  const [includeHistory, setIncludeHistory] = useState(false)
  const [seatTarget, setSeatTarget] = useState<wl.WaitlistEntryDto | null>(null)
  const { data, loading, error, reload } = useAsync(
    (s) => wl.listWaitlist({ includeHistory }, s),
    [includeHistory],
  )

  // Auto-poll cada 20s — la fila cambia rápido.
  useEffect(() => {
    const iv = setInterval(() => reload(), POLL_MS)
    return () => clearInterval(iv)
  }, [reload])

  const [creating, setCreating] = useState(false)

  const sorted = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.addedAt.localeCompare(b.addedAt)),
    [data],
  )

  // Métricas simples del día.
  const stats = useMemo(() => {
    const waiting = sorted.filter((e) => e.status === 'WAITING' || e.status === 'NOTIFIED')
    const seated = sorted.filter((e) => e.status === 'SEATED')
    const left = sorted.filter((e) => e.status === 'LEFT' || e.status === 'EXPIRED')
    const totalCovers = waiting.reduce((s, e) => s + e.partySize, 0)
    // Tiempo promedio de espera (de los sentados ya): seatedAt - addedAt
    const waitTimes = seated
      .filter((e) => e.seatedAt)
      .map((e) => (new Date(e.seatedAt!).getTime() - new Date(e.addedAt).getTime()) / 60_000)
    const avgWaitMin = waitTimes.length > 0
      ? Math.round(waitTimes.reduce((a, b) => a + b, 0) / waitTimes.length)
      : null
    return { waiting, seated, left, totalCovers, avgWaitMin }
  }, [sorted])

  async function handleNotify(e: wl.WaitlistEntryDto) {
    try { await wl.notifyWaitlistEntry(e.id); toast(`${e.name} notificada`, 'success'); reload() }
    catch (err) { toast(err instanceof Error ? err.message : 'No se pudo notificar.', 'danger') }
  }
  async function handleMove(e: wl.WaitlistEntryDto, direction: wl.MoveDirection) {
    try {
      await wl.moveWaitlistEntry(e.id, direction)
      toast(direction === 'UP' ? 'Subió en la fila' : 'Bajó en la fila', 'success')
      reload()
    } catch (err) { toast(err instanceof Error ? err.message : 'No se pudo mover.', 'danger') }
  }
  async function handleNoShow(e: wl.WaitlistEntryDto) {
    try {
      const updated = await wl.attemptNoShowWaitlistEntry(e.id)
      toast(
        updated.status === 'EXPIRED' ? `${e.name} expiró (sin respuesta)` : `Sin respuesta (${updated.noShowAttempts}/3)`,
        updated.status === 'EXPIRED' ? 'warn' : 'success',
      )
      reload()
    } catch (err) { toast(err instanceof Error ? err.message : 'No se pudo registrar.', 'danger') }
  }
  async function handleRemove(e: wl.WaitlistEntryDto) {
    const ok = await confirm({
      title: 'Quitar de la fila',
      message: `¿Quitar a ${e.name} de la fila de espera?`,
      confirmLabel: 'Quitar',
      tone: 'danger',
    })
    if (!ok) return
    try { await wl.leaveWaitlistEntry(e.id); toast('Retirado de la fila', 'success'); reload() }
    catch (err) { toast(err instanceof Error ? err.message : 'No se pudo retirar.', 'danger') }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Cola de espera</h1>
          <p className="adm-page__sub">
            Visible también en el POS (drawer derecho del Plano). Los clientes pueden anotarse desde <strong>fila.nintaigo.com</strong>.
          </p>
        </div>
        <div className="adm-page__head-actions" style={{ display: 'flex', gap: 8 }}>
          <ShareLinkButton
            label="Copiar enlace de cola"
            buildUrl={({ slug }) => (slug ? `https://fila.nintaigo.com/fila/${slug}` : null)}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" checked={includeHistory} onChange={(e) => setIncludeHistory(e.target.checked)} />
            <History size={14} /> Incluir historia
          </label>
          <button type="button" className="adm-btn" onClick={reload} title="Recargar">
            <RefreshCw size={14} />
          </button>
          <button type="button" className="adm-btn adm-btn--primary" onClick={() => setCreating(true)}>
            <Plus size={14} /> Agregar a fila
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, padding: '8px 0' }}>
        <StatCard label="En cola"        value={String(stats.waiting.length)} hint={`${stats.totalCovers} personas`} />
        <StatCard label="Sentadas hoy"   value={String(stats.seated.length)} />
        <StatCard label="Abandonaron"    value={String(stats.left.length)} />
        <StatCard label="Espera promedio" value={stats.avgWaitMin !== null ? `${stats.avgWaitMin} min` : '—'} hint="solo sentadas" />
      </div>

      {asyncState(loading, error, reload)}

      {!loading && !error && (
        <>
          {sorted.length === 0 ? (
            <div className="adm-card"><div className="adm-empty">{includeHistory ? 'Sin actividad hoy.' : 'No hay nadie en la fila.'}</div></div>
          ) : (
            <div className="adm-card" style={{ overflowX: 'auto' }}>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código</th>
                    <th>Cliente</th>
                    <th>Personas</th>
                    <th>Contacto</th>
                    <th>Hace</th>
                    <th>Espera est.</th>
                    <th>Notas</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((e) => {
                    const isActive = e.status === 'WAITING' || e.status === 'NOTIFIED'
                    return (
                      <tr key={e.id} onClick={() => navigate(`/waitlist/${e.id}`)} style={{ cursor: 'pointer' }} title="Abrir ficha de la entrada">
                        <td><strong>{e.position}</strong></td>
                        <td>{e.queueCode ? <span className="adm-badge adm-badge--ok" style={{ fontFamily: 'monospace' }}>{e.queueCode}</span> : <span className="adm-muted">—</span>}</td>
                        <td><strong>{e.name}</strong></td>
                        <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Users size={11} /> {e.partySize}</span></td>
                        <td onClick={(ev) => ev.stopPropagation()}>
                          {e.phone ? (
                            <a href={`tel:${e.phone}`} style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Phone size={11} /> {e.phone}
                            </a>
                          ) : <span className="adm-muted">—</span>}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={11} /> {minutesSince(e.addedAt)} min
                          </span>
                          <div className="adm-muted" style={{ fontSize: 11 }}>{fmtTime(e.addedAt)}</div>
                        </td>
                        <td>{e.estimatedWaitMin !== null ? `${e.estimatedWaitMin} min` : <span className="adm-muted">—</span>}</td>
                        <td className="adm-muted" style={{ maxWidth: 220, fontSize: 11 }}>{e.notes ?? '—'}</td>
                        <td><span className={statusBadgeClass(e.status)}>{statusLabel(e.status)}</span></td>
                        <td onClick={(ev) => ev.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {isActive && (
                              <>
                                {e.status === 'WAITING' && (
                                  <button type="button" className="adm-btn adm-btn--sm" onClick={() => void handleNotify(e)} title="Marcar notificada (mesa lista)">
                                    <Bell size={12} />
                                  </button>
                                )}
                                <button type="button" className="adm-btn adm-btn--sm" onClick={() => setSeatTarget(e)} title="Sentar (abre sesión de mesa)" aria-label="Sentar">
                                  <Armchair size={12} />
                                </button>
                                <button type="button" className="adm-btn adm-btn--sm" disabled={e.position <= 1} onClick={() => void handleMove(e, 'UP')} title="Subir prioridad" aria-label="Subir prioridad">
                                  <ArrowUp size={12} />
                                </button>
                                <button type="button" className="adm-btn adm-btn--sm" onClick={() => void handleMove(e, 'DOWN')} title="Bajar prioridad" aria-label="Bajar prioridad">
                                  <ArrowDown size={12} />
                                </button>
                                <button type="button" className="adm-btn adm-btn--sm" onClick={() => void handleNoShow(e)} title="Sin respuesta (3 intentos → expira)" aria-label="Sin respuesta">
                                  <PhoneOff size={12} />
                                </button>
                                <button type="button" className="adm-btn adm-btn--sm adm-btn--danger" onClick={() => void handleRemove(e)} title="Quitar de fila" aria-label="Quitar de fila">
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              className="adm-btn adm-btn--sm"
                              onClick={() => navigate(`/waitlist/${e.id}`)}
                              title="Abrir ficha"
                              aria-label="Abrir ficha"
                            >
                              <ChevronRight size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {creating && (
        <AddToWaitlistDialog
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); reload() }}
        />
      )}

      {seatTarget && (
        <SeatDialog
          entry={seatTarget}
          onClose={() => setSeatTarget(null)}
          onSeated={() => { toast(`${seatTarget.name} sentada`, 'success'); setSeatTarget(null); reload() }}
        />
      )}
    </div>
  )
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="adm-card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span className="adm-muted" style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: 22, fontWeight: 600, fontFamily: 'monospace' }}>{value}</span>
      {hint && <span className="adm-muted" style={{ fontSize: 10 }}>{hint}</span>}
    </div>
  )
}

function AddToWaitlistDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [partySize, setPartySize] = useState(2)
  const [estimatedWaitMin, setEstimatedWaitMin] = useState<number | ''>('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) { setErr('Nombre requerido'); return }
    setBusy(true); setErr(null)
    try {
      const input: wl.CreateWaitlistInput = { name: name.trim(), partySize }
      if (phone.trim()) input.phone = phone.trim()
      if (notes.trim()) input.notes = notes.trim()
      if (estimatedWaitMin !== '' && estimatedWaitMin > 0) input.estimatedWaitMin = estimatedWaitMin
      await wl.addWaitlistEntry(input)
      onCreated()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al agregar a fila')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="adm-modal-bg" role="dialog" aria-modal>
      <form className="adm-modal" onSubmit={(e) => void handleSubmit(e)} style={{ maxWidth: 440 }}>
        <h3 style={{ margin: 0 }}>Agregar a fila de espera</h3>

        <label className="adm-field">
          <span>Nombre</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus className="adm-input" required />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label className="adm-field">
            <span>Teléfono</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+52..." className="adm-input" />
          </label>
          <label className="adm-field">
            <span>Personas</span>
            <input type="number" min={1} max={50} value={partySize} onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))} className="adm-input" />
          </label>
        </div>

        <label className="adm-field">
          <span>Espera estimada (min, opcional)</span>
          <input
            type="number"
            min={0}
            max={600}
            value={estimatedWaitMin}
            onChange={(e) => setEstimatedWaitMin(e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0))}
            placeholder="ej. 25"
            className="adm-input"
          />
        </label>

        <label className="adm-field">
          <span>Notas (opcional)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Pidió terraza, walk-in..." className="adm-input" />
        </label>

        {err && <div className="adm-banner adm-banner--danger" role="alert">{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="adm-btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="adm-btn adm-btn--primary" disabled={busy}>
            {busy ? 'Guardando…' : 'Agregar'}
          </button>
        </div>
      </form>
    </div>
  )
}
